import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom'; 
import { useTranslation } from 'react-i18next';
import { ArrowRightIcon, CpuChipIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import SchemaMarkup from '../components/SchemaMarkup';
import { projects } from '../content/projects';

export default function Portfolio() {
  const { t, i18n } = useTranslation();
  const langPrefix = i18n.language === 'et' ? '/et' : '/en';
  
  const { lang } = useParams();
  const currentLang = lang || 'et';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const projectConfig = projects;

  return (
    <div className="bg-slate-50 min-h-screen pb-20">
      <SEO 
        title={`${t('case_studies.page_title')} - Mupax`} 
        description={t('case_studies.page_subtitle')}
        url={`/${currentLang}/case-studies`} 
      />
      <SchemaMarkup 
        type="collection" 
        data={{
          title: t('case_studies.page_title'),
          description: t('case_studies.page_subtitle'),
          url: `${langPrefix}/case-studies`
        }} 
      />
      
      <PageHeader 
        title={t('case_studies.page_title')}
        subtitle={t('case_studies.page_subtitle')}
      />

      <div className="container mx-auto px-4 py-12 max-w-6xl">
        <div className="grid md:grid-cols-1 gap-8">
          {projectConfig.map((config) => {
            const project = t(`case_studies.projects.${config.id}`, { returnObjects: true });
            
            if (!project || typeof project !== 'object') return null;

            return (
              <Link 
                to={`/${currentLang}/case-studies/${config.id}`} 
                key={config.id}
                className="group relative bg-white rounded-3xl p-8 border border-slate-200 shadow-sm hover:shadow-2xl hover:border-blue-400 hover:-translate-y-1 transition-all duration-300 flex flex-col md:flex-row gap-8 items-stretch"
              >
                {/* --- LEFT: VISUAL IDENTIFIER --- */}
                <div className="w-full md:w-1/3 bg-slate-100 rounded-2xl min-h-[280px] flex flex-col items-center justify-center relative overflow-hidden border border-slate-200 group-hover:border-blue-300 transition-colors">
                  {(config.thumbnail || config.image) ? (
                    <img
                      src={config.thumbnail || config.image}
                      alt={project.title}
                      width="400"
                      height="280"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover absolute inset-0"
                    />
                  ) : (
                    <>
                      <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(#22d3ee 1px, transparent 1px)', backgroundSize: '24px 24px' }}></div>
                      <div className="relative z-10 p-6 rounded-full bg-slate-800/50 backdrop-blur-sm border border-slate-700">
                        <config.icon className="w-16 h-16 text-blue-400 stroke-1" />
                      </div>
                    </>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-slate-900/70 backdrop-blur-sm px-4 py-2">
                    <span className="text-slate-300 text-xs font-mono tracking-[0.2em] uppercase font-bold">
                      {project.pillar || 'ARCHITECTURE'}
                    </span>
                  </div>
                </div>

                {/* --- RIGHT: CONTENT --- */}
                <div className="w-full md:w-2/3 flex flex-col h-full justify-between py-2">
                  <div>
                    {/* Header */}
                    <div className="flex justify-between items-start mb-2">
                         <h3 className="text-2xl md:text-3xl font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                            {project.title}
                         </h3>
                    </div>
                    
                    <p className="text-blue-700 font-bold text-sm uppercase tracking-wide mb-4 flex items-center gap-2">
                        <CpuChipIcon className="w-4 h-4" />
                        {project.subtitle}
                    </p>

                    <p className="text-slate-600 leading-relaxed mb-6 text-lg">
                      {project.summary}
                    </p>
                    
                    {/* Tech Stack Chips (Monospace for Engineering vibe) */}
                    <div className="flex flex-wrap gap-2 mb-8">
                      {(project.tags || []).map(tag => (
                        <span key={tag} className="px-3 py-1.5 bg-white border border-slate-200 text-slate-600 rounded-lg text-xs font-mono font-semibold uppercase hover:border-blue-400 hover:text-blue-700 transition-colors cursor-default">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Footer: Metrics & Button */}
                  <div className="flex flex-col sm:flex-row items-center justify-between border-t border-slate-100 pt-6 mt-auto gap-6">
                    
                    {/* Metrics */}
                    <div className="flex gap-8 w-full sm:w-auto">
                      {(project.metrics || []).slice(0, 2).map((metric, i) => (
                        <div key={i} className="flex flex-col">
                          <span className="text-2xl font-black text-blue-600 tracking-tight">{metric.value}</span>
                          <span className="text-xs text-slate-600 uppercase font-bold tracking-wider">{metric.label}</span>
                        </div>
                      ))}
                    </div>

                    {/* Button (Styled as a Div because it's inside a Link) */}
                    <div className="bg-blue-600 text-white py-3 px-8 rounded-full font-bold shadow-lg flex items-center gap-2 transition-all group-hover:bg-blue-500 group-hover:shadow-blue-500/30 group-hover:scale-105">
                       <span className="text-sm">{t('home_page.read_more') || 'Read Case Study'}</span>
                       <ArrowRightIcon className="w-4 h-4" />
                    </div>

                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
